import { GameHazard } from "@/lib/types/common/GameMode";

export enum HazardBehavior {
    VISUAL_ONLY = "visual_only",
    CUSTOM_DRAG = "custom_drag",
}

/**
 * Animation types for filter properties
 */
export type FilterAnimationType = 'oscillate' | 'random' | 'pulse';

/**
 * Configuration for animated filter properties
 */
export interface FilterAnimationConfig {
    /** The filter property to animate (e.g., 'brightness', 'blur', 'saturate') */
    property: string;
    /** Minimum value for the animation */
    min: number;
    /** Maximum value for the animation */
    max: number;
    /** Animation type */
    type: FilterAnimationType;
    /** Duration of one animation cycle in ms (for oscillate/pulse) */
    duration?: number;
    /** How often to change the random value in ms (for random type) */
    randomInterval?: number;
    /** Unit for the value (e.g., 'px', '%', or empty for unitless like brightness) */
    unit?: string;
}

export interface HazardDefinition {
    id: GameHazard;
    name: string;
    behavior: HazardBehavior;
    /** Static CSS filter string - used when no animation is needed */
    cssFilter?: string;
    cssTransform?: string;
    /** Animated filter configurations - these will override/complement cssFilter */
    animatedFilters?: FilterAnimationConfig[];
    /** Timer speed multiplier (e.g., 2.0 means timer goes twice as fast) */
    timerMultiplier?: number;
    dragConfig?: {
        yawMultiplier?: number;
        pitchMultiplier?: number;
        yawInverted?: boolean;
        pitchInverted?: boolean;
        smoothFactor?: number;
        yawDisabled?: boolean;
        forcedAutoRotate?: boolean;
        forcedAutoRotateSpeed?: number;
        /** Charmed effect - auto-rotate towards a target position */
        charmedTarget?: boolean;
        charmedRotateSpeed?: number;
        dynamic?: {
            enabled: boolean;
            interval?: number; // for toggling inversion
        };
    };
}

/**
 * Centralized hazard configuration
 * Add new hazards here and they will automatically work throughout the application
 */
export const HAZARD_DEFINITIONS: Record<GameHazard, HazardDefinition> = {
    [GameHazard.PETRIFY]: {
        id: GameHazard.PETRIFY,
        name: 'petrify',
        behavior: HazardBehavior.CUSTOM_DRAG,
        cssFilter: 'grayscale(100%) contrast(90%)',
        dragConfig: {
            yawMultiplier: 0.8,
            pitchMultiplier: 0.8,
            smoothFactor: 0.10,
        },
    },
    [GameHazard.SUBTRACTIVE_PALLETTE]: {
        id: GameHazard.SUBTRACTIVE_PALLETTE,
        name: 'subtractive_palette',
        behavior: HazardBehavior.VISUAL_ONLY,
        cssFilter: 'hue-rotate(180deg)',
    },
    [GameHazard.SKYBLIND]: {
        id: GameHazard.SKYBLIND,
        name: 'skyblind',
        behavior: HazardBehavior.VISUAL_ONLY,
        animatedFilters: [
            {
                property: 'brightness',
                min: 1.4,
                max: 2.5,
                type: 'oscillate',
                duration: 7000,
                randomInterval: 3000,
            },
            {
                property: 'blur',
                min: 4,
                max: 8,
                type: 'oscillate',
                unit: 'px',
                duration: 3000,
                randomInterval: 1000,
            }
        ],
    },
    [GameHazard.BLIND]: {
        id: GameHazard.BLIND,
        name: 'blind',
        behavior: HazardBehavior.VISUAL_ONLY,
    },
    [GameHazard.SLEEP]: {
        id: GameHazard.SLEEP,
        name: 'sleep',
        behavior: HazardBehavior.VISUAL_ONLY,
    },
    [GameHazard.REVERSE]: {
        id: GameHazard.REVERSE,
        name: 'reverse',
        behavior: HazardBehavior.CUSTOM_DRAG,
        cssTransform: 'scaleY(-1)',
        dragConfig: {
            pitchInverted: true,
        },
    },
    [GameHazard.FORCED_MARCH]: {
        id: GameHazard.FORCED_MARCH,
        name: 'forced_march',
        behavior: HazardBehavior.CUSTOM_DRAG,
        dragConfig: {
            yawDisabled: true,
            forcedAutoRotate: true,
            forcedAutoRotateSpeed: 0.175,
        },
    },
    [GameHazard.MINI]: {
        id: GameHazard.MINI,
        name: 'mini',
        behavior: HazardBehavior.CUSTOM_DRAG,
        cssTransform: 'scale(0.1, 0.1)',
        dragConfig: {
            yawMultiplier: 0.25,
            pitchMultiplier: 0.25,
        },
    },
    [GameHazard.TOAD]: {
        id: GameHazard.TOAD,
        name: 'toad',
        behavior: HazardBehavior.VISUAL_ONLY,
        animatedFilters: [
            {
                property: 'saturate',
                min: 200,
                max: 1000,
                type: 'oscillate',
                duration: 4000,
                unit: '%',
            },
            {
                property: 'hue-rotate',
                min: 30,
                max: 330,
                type: 'oscillate',
                duration: 50000,
                unit: 'deg',
            }
        ],
    },
    [GameHazard.HASTE]: {
        id: GameHazard.HASTE,
        name: 'haste',
        behavior: HazardBehavior.CUSTOM_DRAG,
        timerMultiplier: 2.0, // Timer goes twice as fast
        dragConfig: {
            yawMultiplier: 6,
            pitchMultiplier: 6,
        },
        // Haste visual effect is handled via Three.js shader post-processing
    },
    [GameHazard.CHARMED]: {
        id: GameHazard.CHARMED,
        name: 'charmed',
        behavior: HazardBehavior.CUSTOM_DRAG,
        cssFilter: 'saturate(130%) brightness(1.05)',
        dragConfig: {
            charmedTarget: true,
            charmedRotateSpeed: 4.0,
        },
    },
    [GameHazard.PIXELATE]: {
        id: GameHazard.PIXELATE,
        name: 'pixelate',
        behavior: HazardBehavior.VISUAL_ONLY,
        // Pixelate effect is handled via Three.js shader post-processing
    },
    [GameHazard.MASCOT]: {
        id: GameHazard.MASCOT,
        name: 'mascot',
        behavior: HazardBehavior.VISUAL_ONLY,
        // Mascot effect is handled via MascotOverlay component
    },
};

/**
 * Get all hazard definitions as an array
 */
export function getAllHazards(): HazardDefinition[] {
    return Object.values(HAZARD_DEFINITIONS);
}

/**
 * Get a specific hazard definition
 */
export function getHazardDefinition(hazard: GameHazard | string): HazardDefinition | undefined {
    return HAZARD_DEFINITIONS[hazard as GameHazard];
}

/**
 * Get hazards that require custom drag handling
 */
export function getCustomDragHazards(hazards: string[] | GameHazard[]): HazardDefinition[] {
    return hazards
        .map(h => getHazardDefinition(h as GameHazard))
        .filter((def): def is HazardDefinition =>
            def !== undefined && def.behavior === HazardBehavior.CUSTOM_DRAG
        );
}
